import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import MovieCard from "@/components/feature/MovieCard";
import DetailHero from "@/pages/detail/components/DetailHero";
import CastList from "@/pages/detail/components/CastList";
import ShareModal from "@/pages/detail/components/ShareModal";
import { useSeo } from "@/hooks/useSeo";
import { getMovieById, getActorsByMovie, getRelatedMovies, getHotMovies } from "@/utils/movie";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: movie
      ? `${movie.title} (${movie.year}) - 剧情介绍与演员表 - 欲望的诱惑`
      : "影视内容不存在 - 欲望的诱惑",
    description: movie
      ? `${movie.title}详细剧情介绍，演员信息及影视资讯，尽在欲望的诱惑影视内容站。`
      : "抱歉，未找到对应的影视内容，为你推荐站内热门影视作品。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情介绍,${movie.title}演员表,${movie.type},${movie.region},欲望的诱惑`
      : "欲望的诱惑,影视内容,热门影视",
    canonicalPath: `/detail/${id}`,
  });

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1">
          <PageHeader
            title="影视内容不存在"
            description="抱歉，未找到与之对应的影视内容，可能是链接已失效或作品已下架。"
            crumbs={[{ label: "首页", path: "/" }, { label: "影视分类", path: "/category" }, { label: "未找到" }]}
          />
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12">
            <div className="text-center py-8">
              <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到相关影视内容</h2>
              <Link
                href="/category"
                to="/category"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回影视分类
              </Link>
            </div>
            <div className="mt-10">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-5">热门影视推荐</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {getHotMovies(5).map((m: any) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const cast = getActorsByMovie(movie);
  const related = getRelatedMovies(movie, 5);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <DetailHero movie={movie} onShare={() => setShareOpen(true)} />

        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <section className="pb-8 md:pb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-book-open-line text-primary-500"></i>
              剧情介绍
            </h2>
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-7">
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                {movie.summary}
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                《{movie.title}》由{movie.director}执导，{movie.region}出品，汇集{cast.map((a: any) => a.name).join("、")}等演员共同出演。
                作品以{movie.genres.join("、")}题材为基调，通过细腻的人物刻画与节奏把控，呈现出一个值得反复咀嚼的故事。
                如果你喜欢{(movie.tags || []).join("、")}类型的作品，不妨将它加入你的观影清单。
              </p>
            </div>
          </section>

          <CastList cast={cast} />

          <section className="py-8 md:py-10 border-t border-background-200/70">
            <div className="flex items-end justify-between gap-4 mb-5">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-magic-line text-primary-500"></i>
                相关推荐
              </h2>
              <Link
                href="/category"
                to="/category"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1 whitespace-nowrap"
              >
                更多影视
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {related.map((m: any) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>

          <div className="pt-4">
            <Link
              href="/category"
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 text-foreground-800 border border-background-300/70 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回影视分类
            </Link>
          </div>
        </div>
      </main>
      <Footer />

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={movie.title} />
    </div>
  );
}